package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentDetailResponse
import ai.jessie.shared.documents.DocumentHistoryEntryResponse
import ai.jessie.shared.documents.DocumentPaymentRequest
import ai.jessie.shared.documents.DocumentRemovalPreviewResponse
import ai.jessie.shared.documents.DocumentRemovalRequest
import ai.jessie.shared.documents.DocumentSaveRequest
import ai.jessie.shared.documents.DocumentTransitionPreviewResponse
import ai.jessie.shared.documents.DocumentTransitionRequest
import ai.jessie.shared.documents.DocumentVersionRequest
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// UC-020. The processing detail (steps 1-3), versioned details save (B7), transition preview and
// confirmation (steps 7-11, A13), payment (A6-A8), history (step 12, B5) and owner-only removal
// (A11, A12). The server decides every rule; the browser proposes and shows its answers.
interface DocumentWorkflowPort {
    suspend fun detail(organisationId: String, documentId: String): DocumentDetailResponse

    suspend fun history(
        organisationId: String,
        documentId: String,
    ): List<DocumentHistoryEntryResponse>

    suspend fun save(
        organisationId: String,
        documentId: String,
        request: DocumentSaveRequest,
    ): DocumentDetailResponse

    suspend fun previewTransition(
        organisationId: String,
        documentId: String,
        request: DocumentTransitionRequest,
    ): DocumentTransitionPreviewResponse

    suspend fun transition(
        organisationId: String,
        documentId: String,
        request: DocumentTransitionRequest,
    ): DocumentDetailResponse

    suspend fun recordPayment(
        organisationId: String,
        documentId: String,
        request: DocumentPaymentRequest,
    ): DocumentDetailResponse

    suspend fun clearPayment(
        organisationId: String,
        documentId: String,
        request: DocumentVersionRequest,
    ): DocumentDetailResponse

    suspend fun removalPreview(
        organisationId: String,
        documentId: String,
    ): DocumentRemovalPreviewResponse

    suspend fun cancelRemoval(organisationId: String, documentId: String)

    suspend fun remove(organisationId: String, documentId: String, request: DocumentRemovalRequest)
}

val LocalDocumentWorkflowPort =
    staticCompositionLocalOf<DocumentWorkflowPort> {
        error("Document workflow port must be supplied by the application")
    }

class BrowserDocumentWorkflowPort : DocumentWorkflowPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun detail(organisationId: String, documentId: String) =
        json.detailAnswer(documentId, request(organisationId, documentId, "/processing"))

    override suspend fun history(organisationId: String, documentId: String) =
        json.decodeFromString<List<DocumentHistoryEntryResponse>>(
            request(organisationId, documentId, "/history")
        )

    override suspend fun save(
        organisationId: String,
        documentId: String,
        request: DocumentSaveRequest,
    ) =
        json.detailAnswer(
            documentId,
            request(organisationId, documentId, "/details", json.encodeToString(request)),
        )

    override suspend fun previewTransition(
        organisationId: String,
        documentId: String,
        request: DocumentTransitionRequest,
    ) =
        json
            .decodeFromString<DocumentTransitionPreviewResponse>(
                request(
                    organisationId,
                    documentId,
                    "/transitions/preview",
                    json.encodeToString(request),
                )
            )
            .also { require(it.documentId.equals(documentId, ignoreCase = true)) }

    override suspend fun transition(
        organisationId: String,
        documentId: String,
        request: DocumentTransitionRequest,
    ) =
        json.detailAnswer(
            documentId,
            request(organisationId, documentId, "/transitions", json.encodeToString(request)),
        )

    override suspend fun recordPayment(
        organisationId: String,
        documentId: String,
        request: DocumentPaymentRequest,
    ) =
        json.detailAnswer(
            documentId,
            request(organisationId, documentId, "/payment", json.encodeToString(request)),
        )

    override suspend fun clearPayment(
        organisationId: String,
        documentId: String,
        request: DocumentVersionRequest,
    ) =
        json.detailAnswer(
            documentId,
            request(organisationId, documentId, "/payment/clear", json.encodeToString(request)),
        )

    override suspend fun removalPreview(organisationId: String, documentId: String) =
        json
            .decodeFromString<DocumentRemovalPreviewResponse>(
                request(organisationId, documentId, "/removal/preview", EMPTY_BODY)
            )
            .also { require(it.documentId.equals(documentId, ignoreCase = true)) }

    override suspend fun cancelRemoval(organisationId: String, documentId: String) {
        request(organisationId, documentId, "/removal/cancel", EMPTY_BODY, HTTP_NO_CONTENT)
    }

    override suspend fun remove(
        organisationId: String,
        documentId: String,
        request: DocumentRemovalRequest,
    ) {
        request(
            organisationId,
            documentId,
            "/removal",
            json.encodeToString(request),
            HTTP_NO_CONTENT,
        )
    }

    // A body makes the request a same-origin POST; the server checks its Origin (B9).
    private suspend fun request(
        organisationId: String,
        documentId: String,
        path: String,
        body: String? = null,
        success: Int = HTTP_OK,
    ): String {
        require(validOrganisationId(organisationId) && validDocumentId(documentId))
        return apiRequest(
            json,
            "/organisations/$organisationId/documents/$documentId$path",
            body,
            success,
        ) { status, text ->
            json.documentFailure(status, text)
        }
    }
}

// Only the requested document's detail is accepted.
private fun Json.detailAnswer(documentId: String, text: String) =
    decodeFromString<DocumentDetailResponse>(text).also {
        require(it.documentId.equals(documentId, ignoreCase = true))
    }

// B8: the source with the detail's visibility check; inline only for PDF and images, which the
// server enforces again.
internal fun documentContentHref(organisationId: String, documentId: String, inline: Boolean) =
    "/api/v1/organisations/$organisationId/documents/$documentId/content" +
        if (inline) "?disposition=inline" else ""

private const val EMPTY_BODY = "{}"
private const val HTTP_OK = 200
private const val HTTP_NO_CONTENT = 204
